import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, Video, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { verifyVimeoConnection } from '@/hooks/useVimeo';
import { toast } from 'sonner';

export default function VimeoConfigTab() {
  const [folderId, setFolderId] = useState('');
  const [embedDomains, setEmbedDomains] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'checking' | 'connected' | 'error'>('idle');
  const [accountInfo, setAccountInfo] = useState<{ name: string; type: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // Load settings
  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('platform_settings')
        .select('key, value')
        .in('key', ['vimeo_folder_id', 'vimeo_embed_domains']);

      (data || []).forEach((s: any) => {
        if (s.key === 'vimeo_folder_id') setFolderId((s.value as any)?.folder_id || '');
        if (s.key === 'vimeo_embed_domains') setEmbedDomains(((s.value as any)?.domains || []).join(', '));
      });
    }
    load();
  }, []);

  const handleVerify = async () => {
    setConnectionStatus('checking');
    try {
      const result = await verifyVimeoConnection();
      if (result.connected) {
        setConnectionStatus('connected');
        setAccountInfo({ name: result.account_name, type: result.account_type });
      } else {
        setConnectionStatus('error');
      }
    } catch {
      setConnectionStatus('error');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upsert folder ID
      await supabase
        .from('platform_settings')
        .upsert(
          { key: 'vimeo_folder_id', value: { folder_id: folderId.trim() } as any, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );

      // Upsert embed domains
      const domains = embedDomains
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);

      await supabase
        .from('platform_settings')
        .upsert(
          { key: 'vimeo_embed_domains', value: { domains } as any, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );

      toast.success('Configuración de Vimeo guardada');
    } catch {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Conexión Vimeo
          </CardTitle>
          <CardDescription>
            Verifica que el token de API de Vimeo esté correctamente configurado
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleVerify} disabled={connectionStatus === 'checking'} variant="outline" size="sm">
              {connectionStatus === 'checking' ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Verificar Conexión
            </Button>

            {connectionStatus === 'connected' && (
              <div className="flex items-center gap-2">
                <Badge className="bg-success/20 text-success border-success/30 gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Conectado
                </Badge>
                {accountInfo && (
                  <span className="text-sm text-muted-foreground">
                    {accountInfo.name} ({accountInfo.type})
                  </span>
                )}
              </div>
            )}

            {connectionStatus === 'error' && (
              <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" />
                Error de conexión
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Videos</CardTitle>
          <CardDescription>
            Define la carpeta donde se guardan los videos y los dominios permitidos para embed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="folder-id">Vimeo Folder ID</Label>
            <Input
              id="folder-id"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              placeholder="Ej: 12345678 (ID numérico de la carpeta en Vimeo)"
            />
            <p className="text-xs text-muted-foreground">
              Todos los videos subidos se moverán a esta carpeta. Encuéntralo en la URL de tu carpeta en Vimeo.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="embed-domains">Dominios permitidos para embed</Label>
            <Input
              id="embed-domains"
              value={embedDomains}
              onChange={(e) => setEmbedDomains(e.target.value)}
              placeholder="Ej: lider2.lovable.app, tudominio.com"
            />
            <p className="text-xs text-muted-foreground">
              Dominios donde los videos pueden reproducirse (separados por coma). Los videos no serán accesibles desde otros sitios.
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar Configuración
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
